import { useLocation } from "@tanstack/react-router"
import { Link } from "@tanstack/react-router"
import { type LucideIcon } from "lucide-react"
import { motion } from "framer-motion"

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

export interface NavItem {
  title: string
  path: string
  icon?: LucideIcon
  badge?: number
  matchPrefixes?: string[]
  onClick?: () => void
  ref?: React.Ref<HTMLDivElement>
}

export function Navigation({
  items,
  sectionLabel = "NAVIGATION",
}: {
  items: NavItem[]
  sectionLabel?: string
}) {
  const location = useLocation()
  const { state } = useSidebar()
  const isCollapsed = state === "collapsed"

  return (
    <SidebarGroup className={isCollapsed ? "flex items-center justify-center" : ""}>
      <SidebarGroupLabel className="font-semibold text-xs uppercase text-[#0014cc]">
        {sectionLabel}:
      </SidebarGroupLabel>
      <SidebarMenu className={isCollapsed ? "items-center gap-1" : "gap-1"}>
        {items.map((item, index) => {
          const isActive =
            location.pathname === item.path ||
            (item.matchPrefixes?.some(p => location.pathname.startsWith(p)) ?? false)

          return (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.3,
                delay: index * 0.05,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              <SidebarMenuItem
                className={isCollapsed ? "flex justify-center pb-0" : ""}
              >
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="w-full"
                >
                  {(() => {
                    const baseCollapsed = `relative flex w-full items-center gap-2.5 overflow-hidden rounded-md p-2.5 text-left outline-none transition-colors h-12 text-base [&>svg]:h-6 [&>svg]:w-6 [&>svg]:shrink-0 justify-center !h-12 !w-12 !p-2.5 ${
                      isActive
                        ? "bg-[#0014cc] text-white font-medium"
                        : "text-sidebar-foreground hover:bg-slate-100 hover:text-slate-900"
                    }`
                    const baseExpanded = `flex w-full items-center gap-2.5 overflow-hidden rounded-md p-2.5 text-left outline-none transition-colors h-12 text-base [&_svg]:h-6 [&_svg]:w-6 [&_svg]:shrink-0 ${
                      isActive
                        ? "bg-[#0014cc] text-white font-medium"
                        : "text-sidebar-foreground hover:bg-slate-100 hover:text-slate-900"
                    }`
                    const badgeClass = `flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold leading-none ring-2 ${isActive ? "bg-white text-[#0014cc] ring-[#0014cc]" : "bg-[#0014cc] text-white ring-white"}`

                    const inner = isCollapsed ? (
                      <>
                        {item.icon && <item.icon className="w-4 h-4 flex-shrink-0" />}
                        {item.badge != null && item.badge > 0 && (
                          <span className={`absolute top-1 right-1 ${badgeClass}`}>{item.badge}</span>
                        )}
                      </>
                    ) : (
                      <>
                        {item.icon && (
                          <span className="relative flex-shrink-0">
                            <item.icon className="w-4 h-4" />
                            {item.badge != null && item.badge > 0 && (
                              <span className={`absolute -top-1.5 -right-1.5 ${badgeClass}`}>{item.badge}</span>
                            )}
                          </span>
                        )}
                        <span className="font-semibold leading-tight truncate">{item.title}</span>
                      </>
                    )

                    const element = item.onClick ? (
                      <button type="button" onClick={item.onClick} className={isCollapsed ? baseCollapsed : baseExpanded}>
                        {inner}
                      </button>
                    ) : (
                      <Link to={item.path} className={isCollapsed ? baseCollapsed : baseExpanded}>
                        {inner}
                      </Link>
                    )

                    return (
                      <div className="relative" ref={item.ref}>
                        {isCollapsed ? (
                          <Tooltip>
                            <TooltipTrigger asChild>{element}</TooltipTrigger>
                            <TooltipContent side="right" align="center">{item.title}</TooltipContent>
                          </Tooltip>
                        ) : element}
                      </div>
                    )
                  })()}
                </motion.div>
              </SidebarMenuItem>
            </motion.div>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
